import * as React from 'react';
import { FunctionComponent, useEffect } from 'react';
import { Box, IconButton, OutlinedInput, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import styles from './styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TransformIcon from '@mui/icons-material/Transform';

import { UwSubjectivity } from 'types/underwriting.type';
import { UwSummarySubjectivity } from 'application/Models/UwSummarySubjectivity';
import { RouteComponentProps, withRouter } from 'react-router';
import { ROUTES } from 'constants/index';
import { withStyles, WithStyles } from '@mui/styles';

interface Props {
    readonly: boolean;
    onSubjectivitiesChange?: (subjectivities: UwSubjectivity[]) => void;
    initialSubjectivities?: UwSummarySubjectivity[];
    submissionId: string;
}

const UwSubjectivities: FunctionComponent<Props & WithStyles<typeof styles> & RouteComponentProps> = (props) => {
    const { readonly, onSubjectivitiesChange, initialSubjectivities, submissionId, classes, history } = props;

    // Local State
    const [subjectivities, setSubjectivities] = React.useState(initialSubjectivities || []);

    // Keep local state in sync if parent-provided subjectivities change (e.g., pre-seeded defaults or loaded review)
    React.useEffect(() => {
        const areEqual = (a: UwSummarySubjectivity[] = [], b: UwSummarySubjectivity[] = []) =>
            a.length === b.length &&
            a.every((x, i) => x.subjectivityText === b[i]?.subjectivityText && x.submissionId === b[i]?.submissionId);
        if (!areEqual(initialSubjectivities || [], subjectivities)) {
            setSubjectivities(initialSubjectivities || []);
        }
    }, [initialSubjectivities, submissionId]);

    const handleClickAddSubjectivity = () => {
        const newSubs = [...subjectivities, { subjectivityText: '', submissionId: submissionId }];
        setSubjectivities(newSubs);
        onSubjectivitiesChange && onSubjectivitiesChange(newSubs);
    };

    const handleClickRemoveSubjectivity = (index: number) => {
        const newSubs = [...subjectivities];
        newSubs.splice(index, 1);
        setSubjectivities(newSubs);
        onSubjectivitiesChange && onSubjectivitiesChange(newSubs);
    };

    const handleTextInputChange = (event: any, index: number) => {
        const newText = event.target.value;
        const newSubs = [...subjectivities];
        newSubs[index].subjectivityText = newText;
        setSubjectivities(newSubs);
        onSubjectivitiesChange && onSubjectivitiesChange(newSubs);
    };

    return (
        <>
            <Grid
                container
                className={classes.root}
                direction="column"
                justifyContent="center"
                alignItems="stretch"
                spacing={0}
            >
                {!readonly && (
                    <Grid>
                        <Grid container spacing={0} alignItems="center">
                            <Grid>
                                <IconButton
                                    className={classes.addSubjectivityButton}
                                    size="small"
                                    onClick={handleClickAddSubjectivity}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Grid>
                            <Grid>
                                <Typography variant="h4" style={{ marginBottom: 0, marginLeft: 8 }}>
                                    Add Subjectivity
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                <Grid>
                    <>
                        {subjectivities.map((subjectivity, index) => (
                            <Paper key={'subjectivity-' + index} elevation={2} className={classes.subjectivityRoot}>
                                <Grid container justifyContent={'space-between'} alignItems={'center'}>
                                    <Grid xs={11}>
                                        <Box display="flex" justifyContent={'flex-start'} alignItems={'center'}>
                                            <Box marginLeft={'5px'}>
                                                <svg className={classes.subjectivityBullet}>
                                                    <circle />
                                                </svg>
                                            </Box>
                                            <Box flexGrow={1}>
                                                {readonly && <Typography>{subjectivity.subjectivityText}</Typography>}
                                                {!readonly && (
                                                    <OutlinedInput
                                                        readOnly={readonly}
                                                        value={subjectivity.subjectivityText}
                                                        onChange={(event) => handleTextInputChange(event, index)}
                                                        placeholder="Enter Subjectivity"
                                                        className={classes.subjectivityText}
                                                        multiline
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid container justifyContent={'center'} xs={1}>
                                        <Grid>
                                            {subjectivity.submissionId !== submissionId && (
                                                <IconButton
                                                    size={'small'}
                                                    onClick={() =>
                                                        history.push(ROUTES.overview(subjectivity.submissionId))
                                                    }
                                                >
                                                    <TransformIcon></TransformIcon>
                                                </IconButton>
                                            )}
                                            {!readonly && (
                                                <IconButton
                                                    size={'small'}
                                                    onClick={() => handleClickRemoveSubjectivity(index)}
                                                >
                                                    <DeleteOutlineIcon></DeleteOutlineIcon>
                                                </IconButton>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}
                    </>
                </Grid>
            </Grid>
        </>
    );
};

export default withStyles(styles)(withRouter(React.memo(UwSubjectivities)));
